// src/features/patients/infrastructure/api/mappers/screeningReportMapper.ts
import type {
  CalibrationCheck,
  ReportInfoSection,
  ReportMetric,
  ReportMetricUnit,
  ScreeningReport,
} from "@/features/patients/domain/entities/ScreeningReport";

type JsonObject = Record<string, unknown>;

/** Shown for any metric the backend doesn't return yet. */
const DEFAULT_METRIC_VALUE = 0;
/** Neutral percentile used until the backend provides age-matched percentiles. */
const DEFAULT_PERCENTILE = 50;

const isObject = (value: unknown): value is JsonObject =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const safeJsonParse = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
};

const readNumber = (source: unknown, field: string): number | undefined => {
  if (!isObject(source)) return undefined;
  const value = source[field];
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
};

const scale = (value: number | undefined, factor: number) =>
  value === undefined ? undefined : value * factor;

const metric = (
  key: string,
  unit: ReportMetricUnit,
  value?: number,
  percentile?: number,
): ReportMetric => ({
  key,
  unit,
  value: value ?? DEFAULT_METRIC_VALUE,
  percentile: percentile ?? DEFAULT_PERCENTILE,
});

/** Every `<pre>` block in the report holds a JSON payload. */
const extractJsonBlocks = (doc: Document): unknown[] =>
  Array.from(doc.querySelectorAll("pre"))
    .map((pre) => safeJsonParse(pre.textContent ?? ""))
    .filter((value) => value !== undefined);

/** Plain-text table rows (`<th>label</th><td>value</td>`), keyed by lower-cased label. */
const extractTextRows = (doc: Document): Map<string, string> => {
  const rows = new Map<string, string>();
  doc.querySelectorAll("tr").forEach((row) => {
    const label = row.querySelector("th")?.textContent?.trim().toLowerCase();
    const cell = row.querySelector("td");
    if (!label || !cell || cell.querySelector("pre")) return;
    rows.set(label, cell.textContent?.trim() ?? "");
  });
  return rows;
};

const normaliseText = (text: string | null | undefined) =>
  (text ?? "").replace(/\s+/g, " ").trim();

/** Panels (`<h2>` + table) whose rows are plain text rather than JSON payloads. */
const extractInfoSections = (doc: Document): ReportInfoSection[] =>
  Array.from(doc.querySelectorAll(".panel"))
    .map((panel) => ({
      title: normaliseText(panel.querySelector("h2")?.textContent),
      rows: Array.from(panel.querySelectorAll("tr"))
        .filter((row) => !row.querySelector("pre"))
        .map((row) => ({
          label: normaliseText(row.querySelector("th")?.textContent),
          value: normaliseText(row.querySelector("td")?.textContent),
        }))
        .filter((row) => row.label),
    }))
    .filter((section) => section.title && section.rows.length > 0);

const findObjectWithKey = (blocks: unknown[], key: string): JsonObject | undefined =>
  blocks.find((block): block is JsonObject => isObject(block) && key in block);

const findArrayItemWithKey = (blocks: unknown[], key: string): JsonObject | undefined => {
  for (const block of blocks) {
    if (!Array.isArray(block)) continue;
    const match = block.find((item) => isObject(item) && key in item);
    if (match) return match as JsonObject;
  }
  return undefined;
};

const CALIBRATION_CHECKS: CalibrationCheck["key"][] = [
  "visualAcuity",
  "speechArticulation",
  "hearingScreening",
  "eyeCalibration",
];

/**
 * The screening-report endpoint only returns rendered HTML, with the ML
 * outputs embedded as pretty-printed JSON in `<pre>` blocks. Pull those
 * payloads back out and shape them into the report view model.
 *
 * The report layout (sections + metrics) is fixed on the frontend; anything
 * the backend doesn't return yet falls back to DEFAULT_METRIC_VALUE /
 * DEFAULT_PERCENTILE. Payloads are located by their keys rather than section
 * headings so the mapping survives wording changes in the backend template.
 */
export const mapScreeningReportHtml = (html: string): ScreeningReport | null => {
  if (!html || typeof DOMParser === "undefined") return null;

  const doc = new DOMParser().parseFromString(html, "text/html");
  const blocks = extractJsonBlocks(doc);
  const rows = extractTextRows(doc);

  const behaviour = findObjectWithKey(blocks, "response_accuracy");
  const responseTimes = isObject(behaviour?.response_time_metrics)
    ? behaviour.response_time_metrics
    : undefined;
  const decisionRt = readNumber(responseTimes?.decision_response_time, "mean_sec");
  const localisationRt = readNumber(responseTimes?.localisation_response_time, "mean_sec");

  const eye = findObjectWithKey(blocks, "gaze_accuracy_index");

  const memory = findObjectWithKey(blocks, "immediate_recall_response");
  const immediate = memory?.immediate_recall_response;
  const delayed = memory?.delayed_recall_response;
  const immediateRt = readNumber(immediate, "recall_response_time");

  const speech = findObjectWithKey(blocks, "total_repetition_pataka");
  const speechDetails = findArrayItemWithKey(blocks, "pataka_units");

  return {
    assessedOn: rows.get("created at"),
    generatedOn: rows.get("updated at"),
    calibration: CALIBRATION_CHECKS.map((key) => ({ key })),
    infoSections: extractInfoSections(doc),
    domains: [
      {
        key: "visual",
        groups: [
          {
            key: "detectionLocalisation",
            metrics: [
              metric("responseAccuracy", "percent", readNumber(behaviour, "response_accuracy")),
              metric("faceDetectionRate", "percent", readNumber(behaviour, "face_detection_rate")),
              metric(
                "correctRejectionRate",
                "percent",
                readNumber(behaviour, "correct_rejection_rate"),
              ),
              metric("falseAlarmRate", "percent", readNumber(behaviour, "false_alarm_rate")),
              metric("missRate", "percent", readNumber(behaviour, "miss_rate")),
              metric(
                "localisationAccuracy",
                "percent",
                readNumber(behaviour, "localisation_accuracy"),
              ),
              metric("decisionResponseTime", "seconds", decisionRt),
              metric("localisationResponseTime", "seconds", localisationRt),
            ],
          },
          {
            key: "oculomotorGaze",
            metrics: [
              metric(
                "meanFixationDuration",
                "milliseconds",
                scale(readNumber(eye, "mean_fixation_duration_sec"), 1000),
              ),
              metric("gazeAccuracyIndex", "index", readNumber(eye, "gaze_accuracy_index")),
              metric(
                "timeToFirstFixation",
                "milliseconds",
                scale(readNumber(eye, "time_to_first_fixation_sec"), 1000),
              ),
              metric("aoiDwellTime", "seconds", readNumber(eye, "aoi_dwell_time_sec")),
              metric("fixationCount", "average", readNumber(eye, "fixation_count")),
            ],
          },
        ],
      },
      {
        key: "speech",
        groups: [
          {
            key: "detectionLocalisation",
            metrics: [
              metric("speechRate", "syllablesPerSecond", readNumber(speech, "speech_rate")),
              metric(
                "totalPatakaRepetitions",
                "count",
                readNumber(speech, "total_repetition_pataka"),
              ),
              metric("responseLatency", "milliseconds"),
              metric("interWordPauseDuration", "milliseconds"),
              metric("speechDuration", "seconds", readNumber(speechDetails, "speech_duration")),
            ],
          },
        ],
      },
      {
        key: "memory",
        groups: [
          {
            key: "immediateRecall",
            metrics: [
              metric("recallAccuracy", "percent", readNumber(immediate, "unordered_recall_accuracy")),
              metric("recallResponseTime", "seconds", immediateRt),
            ],
          },
          {
            key: "delayedRecall",
            metrics: [
              metric("recallAccuracy", "percent", readNumber(delayed, "unordered_recall_accuracy")),
              metric("recallResponseTime", "seconds", readNumber(delayed, "recall_response_time")),
            ],
          },
        ],
      },
      {
        key: "reactionTime",
        groups: [
          {
            metrics: [
              metric("decisionRt", "seconds", decisionRt),
              metric("localisationRt", "seconds", localisationRt),
              metric("speechLatency", "seconds"),
              metric("recallRt", "seconds", immediateRt),
            ],
          },
        ],
      },
    ],
  };
};
